import React, { useEffect } from 'react';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';

interface Props {
  open:      boolean;
  loading:   boolean;
  onConfirm: () => void;
  onCancel:  () => void;
}

export function ConfirmModal({ open, loading, onConfirm, onCancel }: Props) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape' && !loading) onCancel(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      style={{
        position:        'fixed',
        inset:           0,
        backgroundColor: 'rgba(0,0,0,0.72)',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        zIndex:          1000,
        padding:         spacing.md,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !loading) onCancel(); }}
    >
      <div
        style={{
          backgroundColor: colors.neutral.charcoal,
          borderRadius:    borderRadius.xl,
          padding:         spacing.xl,
          maxWidth:        '440px',
          width:           '100%',
          boxShadow:       shadows.xl,
        }}
      >
        {/* Warning icon */}
        <div style={{ textAlign: 'center', marginBottom: spacing.md }}>
          <span style={{ fontSize: '40px', lineHeight: 1 }}>⚠️</span>
        </div>

        {/* Title — BM primary, EN secondary */}
        <h2
          id="confirm-modal-title"
          style={{
            margin:     `0 0 ${spacing.xs}`,
            fontSize:   typography.fontSize.xl,
            fontWeight: typography.fontWeight.bold,
            color:      colors.neutral.white,
            textAlign:  'center',
          }}
        >
          Set Semula Data Demo?
        </h2>
        <p style={{ margin: `0 0 ${spacing.lg}`, fontSize: typography.fontSize.sm, color: colors.neutral.gray400, textAlign: 'center' }}>
          Reset Demo Data?
        </p>

        {/* Body */}
        <div
          style={{
            backgroundColor: 'rgba(247,208,0,0.08)',
            border:          `1px solid rgba(247,208,0,0.25)`,
            borderRadius:    borderRadius.md,
            padding:         spacing.md,
            marginBottom:    spacing.xl,
          }}
        >
          <p style={{ margin: `0 0 ${spacing.sm}`, fontSize: typography.fontSize.sm, color: colors.neutral.gray200 }}>
            Semua data demo akan dipadam dan ditetapkan semula kepada keadaan asal. Tindakan ini tidak boleh dibatalkan.
          </p>
          <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
            All demo data will be wiped and restored to its baseline state. This action cannot be undone.
          </p>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <Button
            variant="ghost"
            size="md"
            onClick={onCancel}
            disabled={loading}
          >
            Batal / Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={onConfirm}
            loading={loading}
          >
            Set Semula / Reset
          </Button>
        </div>
      </div>
    </div>
  );
}
